import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    SettingOption,
    TibboDataType,
    SettingControlType,
} from '@appblocks/codegeneration/types';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import CggTelemetry from '@appblocks/codegeneration/Libraries/CggTelemetry';
import LUISGroup from '@appblocks/codegeneration/SettingsConfigurations/LUISGroup';
import { extractDomain } from '@appblocks/utils';
import * as micropython from './micropython';
import * as zephyr from './zephyr';
import * as tios from './tios';
import * as simulator from './simulator';


export default class Cgg extends BaseFeature {
    constructor() {
        super();
        this.name = 'AppBlocks Cloud';
        this.icon = 'IoMdCloudOutline';
        this.id = 'cgg';
        this.requirements = ['network'];

        const settings = [
            {
                id: 'GENERAL',
                name: 'General',
                settingType: SettingType.GROUP,
                default: '',
            },
            {
                id: 'SETTINGS',
                name: 'Settings',
                settingType: SettingType.GROUP,
                default: '',
            },
            {
                id: 'CONSOLE',
                name: 'Console',
                settingType: SettingType.GROUP,
                default: '',
            },
            {
                id: 'ADVANCED',
                name: 'Advanced',
                settingType: SettingType.GROUP,
                default: '',
            },
            {
                id: 'ENABLED',
                name: 'Enabled',
                settingType: SettingType.OPTION,
                required: true,
                group: 'GENERAL',
                default: '0',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'SERVER',
                name: 'Server Address',
                settingType: SettingType.STRING,
                dataType: TibboDataType.SETTING,
                group: 'ADVANCED',
                setting: {
                    SETTINGID: 'CGGSA',
                    NAME: 'CGG Server Address',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    DEFAULT: process.env.CGG_SERVER || extractDomain(process.env.API_URL || 'api.appblocks.io'),
                },
                UIControl: {
                    CONTROL: 'EDIT',
                },
                required: true,
                default: process.env.CGG_SERVER || extractDomain(process.env.API_URL || 'api.appblocks.io'),
            },
            {
                id: 'SERVER_PORT',
                name: 'Server Port',
                settingType: SettingType.HIDDEN,
                group: 'ADVANCED',
                required: true,
                default: '1883',
            },
            {
                id: 'USERNAME',
                name: 'Device ID',
                settingType: SettingType.STRING,
                dataType: TibboDataType.SETTING,
                group: 'GENERAL',
                setting: {
                    SETTINGID: 'CGGID',
                    NAME: 'AppBlocks Cloud Device ID',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                },
                UIControl: {
                    CONTROL: 'EDIT',
                },
                required: true,
                default: '',
            },
            {
                id: 'PASSWORD',
                name: 'Device Password',
                settingType: SettingType.STRING,
                dataType: TibboDataType.SETTING,
                group: 'GENERAL',
                setting: {
                    SETTINGID: 'CGGP',
                    NAME: 'Device Password',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                },
                UIControl: {
                    CONTROL: 'EDIT',
                },
                required: true,
                default: '',
            },
            {
                id: 'DEBUG_PRINT',
                name: 'Debug',
                settingType: SettingType.OPTION,
                group: 'ADVANCED',
                default: '0',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'WIDGETS',
                name: 'Dashboard Widgets',
                settingType: SettingType.ARRAY,
                required: true,
                controlType: SettingControlType.WIDGETS,
                default: new Array<BaseFeature>(0),
            },
            {
                id: 'INTERVAL',
                name: 'Console Refresh (seconds)',
                settingType: SettingType.NUMBER,
                required: true,
                group: 'ADVANCED',
                min: 1,
                max: 300,
                default: '1',
            },
            {
                id: 'TLS',
                name: 'TLS Enabled',
                settingType: SettingType.OPTION,
                group: 'ADVANCED',
                default: '0',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'GROUPS',
                name: 'Groups',
                settingType: SettingType.ARRAY,
                refId: 'luis_group',
                required: true,
                default: [
                    new LUISGroup(),
                ],
                controlType: SettingControlType.TABS,
            },
            {
                id: 'CONSOLE_PREVIEW',
                name: 'Console Preview',
                settingType: SettingType.CONSOLE_PREVIEW,
                group: 'CONSOLE',
                default: 'cloud',
            },
            {
                id: 'TABLES',
                name: 'Tables',
                settingType: SettingType.ARRAY,
                refId: 'console_table_item',
                required: true,
                default: new Array<BaseFeature>(0),
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getFiles() {
        if (this.project.runtime === 'micropython') {
            return [
                'cgg.py',
            ];
        }
        if (this.project.runtime === 'zephyr') {
            return [
                'src/cgg_client.cpp',
                'src/cgg_client.h',
                'src/cgg_schema.h',
                'src/cgg_console.json',
            ];
        }
        const files = [
            // 'libraries/cgg/cgg.tbh',
            // 'libraries/cgg/cgg.tbs',
            // 'congregate.xtxt',
            'cgg_console.json',
        ];
        return [
            'cgg_console.json',
        ];
    }

    getDependencies() {
        if (this.project.runtime === 'zephyr') {
            return [
                'mqtt_lib',
                'stg',
                'log',
            ];
        }
        const dependencies = [
            'mqtt_lib',
            'stg',
            'log',
        ];

        if (this.project.runtime === 'tios') {
            dependencies.push('firmware_upgrade');
        }

        if (this.project.runtime === 'micropython'
            || this.project.runtime === 'simulator'
        ) {
            return ['stg', 'mqtt_lib'];
        }

        return dependencies;
    }

    getVariables(): Array<string> {
        const results: string[] = [];
        const widgets = this.getSettingValue('WIDGETS');
        for (let i = 0; i < widgets.length; i++) {
            const variable = this.project.getFeature(widgets[i].getSettingValue('VARIABLE'));
            if (!variable) {
                continue;
            }
            const variableName = variable.getSettingValue('VARNAME');
            if (!results.includes(variableName)) {
                results.push(variableName);
            }
        }
        const GROUPS = this.getSettingValue('GROUPS');
        for (let i = 0; i < GROUPS.length; i++) {
            const SETTINGS = GROUPS[i].getSettingValue('SETTINGS');
            for (let j = 0; j < SETTINGS.length; j++) {
                const item = SETTINGS[j];
                const settingFeatureID = item.getSettingValue('SETTINGID');
                const settingFeature = this.project.getFeature(settingFeatureID);
                if (!settingFeature) {
                    continue;
                }
                const variableName = settingFeature.getSettingValue('SETTINGID');
                if (!results.includes(variableName)) {
                    results.push(variableName);
                }
            }
        }
        return results;
    }

    getDefines(): Array<Define> {
        const defines: Array<Define> = [];
        if (this.getSettingValue('DEBUG_PRINT').toString() === '1') {
            defines.push({
                name: 'CGG_DEBUG_PRINT',
                value: '1',
            });
            defines.push({
                name: 'MQTT_DEBUG_PRINT',
                value: '1',
            });
        }

        return defines;
    }

    validate(): Array<ValidationMessage> {
        const messages: Array<ValidationMessage> = [];
        const MQTT = this.project.getFeature('mqtt');
        if (MQTT && MQTT.getEnabled()) {
            messages.push({
                featureId: this.getID(),
                messageType: ValidationMessageType.ERROR,
                message: 'MQTT Library is enabled. AppBlocks Cloud will not work with MQTT enabled.',
            });
        }
        return messages;
    }

    getState(settingId: string): SettingState {
        if (settingId !== 'ENABLED' && this.getSettingValue('ENABLED').toString() === '0') {
            return SettingState.HIDDEN;
        }
        switch (settingId) {
            case 'ENABLED':
                return SettingState.ENABLED;
            default:
                return SettingState.ENABLED;
        }
    }

    getCodeBlocks(): CodeBlock[] {
        switch (this.project.runtime) {
            case 'micropython':
                return micropython.getCodeBlocks(this);
            case 'zephyr':
                return zephyr.getCodeBlocks(this);
            case 'simulator':
                return simulator.getCodeBlocks(this);
            default:
                return tios.getCodeBlocks(this);
        }
    }
}
